import {
  escapeHtml,
  resolveTextBlockContent,
  TextBlock,
} from '@/components/blocks/primitives/text-block';
import { ContentDate } from '@/components/page-sections/content-date';
import { formatProfileNames, normalizeProfiles } from '@/components/page-sections/profile-list';
import { Badge } from '@/components/ui/badge';
import { Heading3, Paragraph } from '@/components/ui/typography';
import type { CollectionCardAlign, CollectionItem } from '@/lib/blocks/collection/blocks';
import { getAlignmentClassName } from '@/lib/blocks/collection/blocks';
import {
  collectionCardBadgeTextClass,
  isValidDateString,
  readCollectionFieldNames,
} from '@/lib/blocks/collection/field-utils';
import { readTrimmedString } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';

export function BadgeListField({
  names,
  align,
  widget = false,
  overlay = false,
  size = 'sm',
  className,
}: {
  names: string[];
  align: CollectionCardAlign;
  widget?: boolean;
  overlay?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}) {
  if (names.length === 0) return null;

  const inverseBadgeClassName =
    'h-auto max-w-full overflow-visible whitespace-normal break-words rounded-[6px] border border-current/30 bg-white/10 px-2.5 py-0.5 font-medium text-inherit';

  const collectionBadgeClassName = cn(
    'h-auto max-w-full overflow-visible whitespace-normal break-words rounded-[6px] border border-current/24 bg-current/[0.04] px-1.5 py-0.5 text-inherit',
    collectionCardBadgeTextClass,
  );

  const badgeProps = overlay
    ? {
        size,
        colorScheme: 'secondary' as const,
        variant: 'solid' as const,
        className: inverseBadgeClassName,
      }
    : {
        size: widget ? 'sm' : size,
        colorScheme: 'secondary' as const,
        variant: 'solid' as const,
        className: collectionBadgeClassName,
      };

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', getAlignmentClassName(align), className)}
    >
      {names.map((name) => (
        <Badge key={name} {...badgeProps}>
          {name}
        </Badge>
      ))}
    </div>
  );
}

export function fallbackDisplayDate(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.toUpperCase();
  }

  return null;
}

export function renderRichTitle(value: unknown, className?: string) {
  const text = readTrimmedString(value);
  if (!text) return null;

  return <Heading3 className={cn('leading-tight', className)}>{text}</Heading3>;
}

export function renderRichSummary(value: unknown, className?: string) {
  const text = readTrimmedString(value);
  if (!text) return null;

  return <Paragraph className={className}>{text}</Paragraph>;
}

export function renderWidgetTitle(value: unknown) {
  const content = resolveTextBlockContent(value).trim();
  if (!content) return null;

  const wrapped = /^\s*</.test(content) ? content : `<h3>${escapeHtml(content)}</h3>`;

  return (
    <TextBlock
      content={wrapped}
      className="[&_.prose]:max-w-none [&_.prose_p]:m-0 [&_.prose_:is(h1,h2,h3,h4,h5,h6)]:my-0"
    />
  );
}

export function renderWidgetSummary(value: unknown, className?: string) {
  return (
    <TextBlock
      content={value}
      className={cn('[&_.prose]:max-w-none [&_.prose_p]:m-0', className)}
    />
  );
}

export function renderBlogMetaRow({
  item,
  showAuthors,
  showPublishedAt,
  includeInvalidDateFallback,
  className,
}: {
  item: CollectionItem;
  showAuthors: boolean;
  showPublishedAt: boolean;
  includeInvalidDateFallback: boolean;
  className: string;
}) {
  const authorNames = showAuthors
    ? readCollectionFieldNames(item.authors).map((name) => name.toUpperCase())
    : [];

  const publishedAtValue = item.publishedAt;
  const hasValidPublishedAt = showPublishedAt && isValidDateString(publishedAtValue);
  const fallbackPublishedAt =
    includeInvalidDateFallback && showPublishedAt && !hasValidPublishedAt
      ? fallbackDisplayDate(publishedAtValue)
      : null;
  const hasPublishedAt = hasValidPublishedAt || fallbackPublishedAt !== null;

  if (authorNames.length === 0 && !hasPublishedAt) return null;

  return (
    <Paragraph className={className}>
      {authorNames.join(' • ')}
      {authorNames.length > 0 && hasPublishedAt ? ' • ' : null}
      {hasValidPublishedAt ? <ContentDate value={publishedAtValue} /> : fallbackPublishedAt}
    </Paragraph>
  );
}

export function renderPinnedBlogMetaRow({
  item,
  showAuthors,
  showPublishedAt,
}: {
  item: CollectionItem;
  showAuthors: boolean;
  showPublishedAt: boolean;
}) {
  const authorLabel = showAuthors
    ? formatProfileNames(normalizeProfiles(item.authors as { name?: string }[] | undefined))
    : '';
  const publishedAtValue = item.publishedAt;
  const hasValidPublishedAt = showPublishedAt && isValidDateString(publishedAtValue);

  if (!authorLabel && !hasValidPublishedAt) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-white/80',
      )}
    >
      {authorLabel ? <span>{authorLabel}</span> : null}
      {authorLabel && hasValidPublishedAt ? <span aria-hidden="true">•</span> : null}
      {hasValidPublishedAt ? <ContentDate value={publishedAtValue} /> : null}
    </div>
  );
}
